import {Injectable, OnDestroy, inject} from '@angular/core';
import {Subscription, timer} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ExplorerService, INode} from 'filemanager-explorer';
import {FilemanagerApiService} from '@services/api/filemanager/filemanager-api.service';
import {RagStatus} from '@interfaces/explorer-entity';

/**
 * Aggiorna lo stato di indicizzazione RAG dei documenti nella cartella corrente
 * mentre il worker li elabora in background.
 *
 * Requisiti soddisfatti:
 *  - il polling NON mostra mai il loader (getList con loaderShow=false);
 *  - il polling parte solo quando nella cartella c'è almeno un documento 'pending';
 *  - i file vengono aggiornati in place, senza ricaricare/cambiare la cartella.
 *
 * È fornito a livello del FilemanagerComponent, così il suo ciclo di vita segue
 * quello della pagina (init on create, stop on destroy).
 */
@Injectable()
export class RagStatusPollingService implements OnDestroy {
  /** Intervallo tra due letture di stato. */
  private readonly POLL_INTERVAL_MS = 4000;

  private explorerService = inject(ExplorerService);
  private filemanagerApi = inject(FilemanagerApiService);

  /** Cartella attualmente aperta nell'explorer. */
  private currentDir?: INode;
  /** Sottoscrizione al timer di polling (attiva solo quando serve). */
  private pollSub?: Subscription;
  /** Sottoscrizione al cambio cartella. */
  private dirSub?: Subscription;

  /** Avvia l'osservazione della cartella aperta. Chiamare una sola volta. */
  init(): void {
    if (this.dirSub) {
      return;
    }
    this.dirSub = this.explorerService.openedDir$.subscribe((dir) => {
      this.currentDir = dir ?? undefined;
      // A ogni cambio/ricarica cartella il polling precedente non è più valido:
      // lo fermo e lo riavvio solo se qui ci sono documenti pending.
      this.stopPolling();
      this.syncPolling();
    });
  }

  /** Avvia il polling solo se nella cartella corrente c'è almeno un documento pending. */
  private syncPolling(): void {
    if (this.pollSub || !this.hasPending()) {
      return;
    }

    const dirAtStart = this.currentDir!;
    const parentId = this.parentEntityId(dirAtStart);

    this.pollSub = timer(this.POLL_INTERVAL_MS, this.POLL_INTERVAL_MS)
      .pipe(
        // loaderShow = false: il polling non deve MAI mostrare il loader.
        switchMap(() => this.filemanagerApi.getList(parentId, '-created_at', false)),
      )
      .subscribe({
        next: (response) => {
          // L'utente ha cambiato cartella nel frattempo: interrompo.
          if (!this.currentDir || this.currentDir !== dirAtStart) {
            this.stopPolling();
            return;
          }
          this.applyStatuses((response?.data ?? []) as any[]);
          if (!this.hasPending()) {
            this.stopPolling();
          }
        },
        error: () => this.stopPolling(),
      });
  }

  /**
   * Copia lo stato RAG aggiornato sui nodi già presenti nella cartella corrente,
   * senza sostituire gli oggetti: le view (CD default) rileggono node.data al
   * tick del timer e mostrano il nuovo stato, restando nella stessa cartella.
   */
  private applyStatuses(items: any[]): void {
    const children = this.currentDir?.children ?? [];
    const byUuid = new Map<string, any>(items.map((it) => [it['uuid'], it]));
    children.forEach((node) => {
      if (!node.isLeaf) {
        return;
      }
      const fresh = byUuid.get(node.data?.['uuid']);
      if (!fresh) {
        return;
      }
      node.data['rag_status'] = fresh['rag_status'];
      node.data['rag_document_id'] = fresh['rag_document_id'] ?? null;
      if (fresh['rag_error'] !== undefined) {
        node.data['rag_error'] = fresh['rag_error'];
      }
    });
  }

  /** Vero se nella cartella corrente c'è almeno un file in stato pending. */
  private hasPending(): boolean {
    return (this.currentDir?.children ?? []).some(
      (c) => c.isLeaf && c.data?.['rag_status'] === RagStatus.PENDING,
    );
  }

  /** Id entità (backend) della cartella corrente, o null per la root. */
  private parentEntityId(dir: INode): number | null {
    const data = dir?.data ?? {};
    return data && data['id'] != null ? (data['id'] as number) : null;
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.dirSub?.unsubscribe();
    this.dirSub = undefined;
  }
}
